import getFormattedDate from '../../utils/getFormattedDate';
import prettyBytes from '../../utils/prettyBytes';
import useDataAttribute from '../infrastructure/DataAttributeContext';
import * as style from './ArticleInfo.module.css';

export interface ArticleInfoProps {
    wineMagazineEdition?: {
        releaseNumber: string;
        pdf?: {
            size: number;
            url: string;
        };
    };
    originalTitle?: string;
    authors?: {
        name?: string;
        role?: string;
        slug?: { current?: string };
    }[];
    chef?: {
        name?: string;
        role?: string;
    };
    lastUpdated?: string;
    firstPublished?: string;
    contactEmail?: string;
    foto?: string;
}

export function ArticleInfo({
    wineMagazineEdition,
    authors,
    chef,
    lastUpdated,
    firstPublished,
    originalTitle,
    contactEmail,
    foto,
}: ArticleInfoProps) {
    const dataAttribute = useDataAttribute();

    if (!wineMagazineEdition && !authors && !chef && !lastUpdated && !firstPublished && !originalTitle && !contactEmail) return null;

    return (
        <p className={style.articleInfo}>
            {!!wineMagazineEdition?.pdf?.url && (
                <span className={style.noBreak}>
                    Først publisert i{' '}
                    <a href={`${wineMagazineEdition.pdf.url}?dl=`} download>
                        Vinbladet {wineMagazineEdition.releaseNumber} (pdf, {prettyBytes(wineMagazineEdition.pdf.size)})
                    </a>
                </span>
            )}
            {!!originalTitle && (
                <span key="originalTitle">
                    <strong>Originaltittel:</strong> {originalTitle}
                </span>
            )}
            {!!authors?.length && (
                <span data-sanity={dataAttribute?.('authors')}>
                    <strong>Tekst:</strong> {authors?.map(nameAndRole)}
                </span>
            )}
            {!!chef?.name && (
                <span data-sanity={dataAttribute?.(['recipe', 'chef'])}>
                    <strong>Oppskrift:</strong> {nameAndRole(chef)}
                </span>
            )}
            {!!firstPublished && (
                <span key="firstPublished">
                    <strong>Publisert:</strong> <time dateTime={firstPublished}>{getFormattedDate(firstPublished)}</time>
                </span>
            )}
            {!!lastUpdated && lastUpdated.split('T')[0] !== firstPublished && (
                <span key="lastUpdated">
                    <strong>Sist oppdatert:</strong> <time dateTime={lastUpdated}>{getFormattedDate(lastUpdated)}</time>
                </span>
            )}
            {!!contactEmail && (
                <span key="contactEmail">
                    Kontakt: <a href={`mailto: ${contactEmail}`}>{contactEmail}</a>
                </span>
            )}
            {!!foto && (
                <span key="foto">
                    <strong>Foto:</strong> {foto}
                </span>
            )}
        </p>
    );
}

function nameAndRole({ name, role, slug }: { name?: string; role?: string; slug?: { current?: string } }, index = 0) {
    return (
        <>
            {index > 0 ? ', ' : ''}
            {slug?.current ? <a href={`/forfatter/${slug.current}`}>{name}</a> : name}
            {role && ` - ${role}`}
        </>
    );
}
